// src/components/PackingLists/PackingList.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, deleteDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';
import ErrorBanner from '../ErrorBanner';
import { Button } from 'react-bootstrap';

// Import components
import ListHeader from './ListHeader';
import ListPhoto from './ListPhoto';
import ProgressBar from './ProgressBar';
import ViewControls from './ViewControls';
import AddItemForm from './AddItemForm';
import GroupedView from './GroupedView';
import LocationModal from './LocationModal';
import CategoryModal from './CategoryModal';
import EditPhotoModal from './EditPhotoModal';

// Import utility functions
import { getMediumImageUrl, getBaseFilenameFromUrl } from './ImageUtils';

// Import styles
import './PackingList.css';
import './StorageLocations.css';

/**
 * PackingList component displays and manages a packing list with items
 * 
 * @param {Object} props - Component props
 * @param {Object|null} props.user - Current authenticated user object or null if not logged in
 * @param {boolean} props.viewOnly - Whether the list should be displayed in view-only mode
 * @returns {JSX.Element} Rendered component
 */
const PackingList = ({ user, viewOnly = false }) => {
  const { listId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [list, setList] = useState(null);
  const [listDoc, setListDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    quantity: 1,
    location: '',
    category: '',
    packed: false
  });
  const [storageLocations, setStorageLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isEditPhotoModalOpen, setIsEditPhotoModalOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [locationInput, setLocationInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState('none');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [viewDensity, setViewDensity] = useState('relaxed');
  const [editingItemId, setEditingItemId] = useState(null);

  // Fetch list data
  useEffect(() => {
    const fetchList = async () => {
      try {
        // Create a query to find the list by its publicId
        const q = query(collection(db, "lists"), where("publicId", "==", listId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0];
          const listData = { id: docData.id, ...docData.data() };
          
          // Set the list document reference for future use
          setListDoc(docData.ref);
          
          // Set the list data
          setList(listData);
          
          // Determine ownership
          if (viewOnly) {
            setIsOwner(false);
          } else if (user && listData.userId) {
            setIsOwner(user.uid === listData.userId);
          } else {
            setIsOwner(false);
          }
        } else {
          console.error("List not found");
          navigate('/');
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching list:", error);
        setError("Failed to load the packing list. Please try again.");
        setLoading(false);
      }
    };
    
    // In view-only mode, we can fetch without a user
    if (listId && (user || viewOnly)) {
      fetchList();
    }
  }, [listId, user, navigate, viewOnly]);
  
  // Fetch storage locations
  useEffect(() => {
    const fetchStorageLocations = async () => {
      if (!list?.id || !isOwner) return;
      
      try {
        const listRef = doc(db, "lists", list.id);
        const storageLocationsCollection = collection(listRef, "storageLocations");
        const storageLocationsSnapshot = await getDocs(storageLocationsCollection);

        const locations = storageLocationsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));

        setStorageLocations(locations);
      } catch (error) {
        console.error("Error fetching storage locations:", error);
        setError("Failed to load storage locations. Please try again.");
      }
    };

    // Only fetch storage locations when we have both the list and know the user is the owner
    if (list && isOwner) {
      fetchStorageLocations();
    }
  }, [list?.id, isOwner]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!list?.id || !isOwner) return;
      
      try {
        const listRef = doc(db, "lists", list.id);
        const categoriesCollection = collection(listRef, "categories");
        const categoriesSnapshot = await getDocs(categoriesCollection);

        const fetchedCategories = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));

        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setError("Failed to load categories. Please try again.");
      }
    };

    // Only fetch categories when we have both the list and know the user is the owner
    if (list && isOwner) {
      fetchCategories();
    }
  }, [list?.id, isOwner]);

  // Reset collapsed states when changing the groupBy mode
  useEffect(() => {
    setCollapsedGroups({});
  }, [groupBy]);

  // Delete associated images from storage
  const deleteImages = async (url) => {
    if (!url) return;
    try {
      const baseFilename = getBaseFilenameFromUrl(url);
      if (!baseFilename) {
        console.log('Error: Could not extract base filename from URL:', url);
        return;
      }
      console.log('Attempting to delete images with base filename:', baseFilename);
      const fileDir = baseFilename.includes('/') ? baseFilename.substring(0, baseFilename.lastIndexOf('/')) : '';
      const fileName = baseFilename.includes('/') ? fileName.substring(baseFilename.lastIndexOf('/') + 1) : baseFilename;
      const fileExt = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      
      // Create references to both resized versions
      const mediumRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_medium${fileExt}`);
      const thumbRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_thumb${fileExt}`);
      
      // Delete both files
      await Promise.all([
        deleteObject(mediumRef).catch(err => console.log('Medium file may not exist:', err)),
        deleteObject(thumbRef).catch(err => console.log('Thumb file may not exist:', err))
      ]);
      console.log('Successfully deleted image files');
    } catch (error) {
      console.error('Error deleting images:', error);
      // Continue even if image deletion fails
    }
  };

  // Handle edit button click
  const handleEditPhoto = () => {
    setEditingList({...list});
    setIsEditPhotoModalOpen(true);
  };

  const savePhotoChanges = async (e) => {
    e.preventDefault();
    if (!listDoc || !isOwner) return;
    
    try {
      const updatedFields = {
        name: editingList.name,
        tripType: editingList.tripType || '',
        photo: editingList.photo || null,
        lastEdited: Timestamp.now()
      };
      
      await updateDoc(listDoc, updatedFields);
      
      // If photo was removed, delete the old images
      if (list.photo && (!editingList.photo || list.photo !== editingList.photo)) {
        await deleteImages(list.photo);
      }
      
      setList({
        ...list,
        ...updatedFields
      });
      setIsEditPhotoModalOpen(false);
    } catch (error) {
      console.error("Error updating list details:", error);
      setError("Failed to update the list details. Please try again.");
    }
  };

  // List saving
  const saveList = async (updatedList) => {
    if (!listDoc) return;
    
    try {
      const listToSave = {
        ...updatedList,
        lastEdited: Timestamp.now()
      };
      
      await setDoc(listDoc, listToSave);
      setList(listToSave);
    } catch (error) {
      console.error("Error saving list:", error);
      setError("Failed to save changes. Please try again.");
    }
  };

  // Item functions
  const addItem = async (e) => {
    e.preventDefault();
    if (!listDoc || !newItem.name || !isOwner) return;

    try {
      const itemToAdd = {
        id: uuidv4(),
        name: newItem.name,
        quantity: newItem.quantity,
        location: newItem.location || '', // Location is now optional
        category: newItem.category,
        packed: false
      };

      const updatedList = {
        ...list,
        items: [...list.items, itemToAdd],
        lastEdited: Timestamp.now()
      };

      await saveList(updatedList);

      // Reset form, but keep location and category
      setNewItem({
        name: '',
        quantity: 1,
        location: '',
        category: '',
        packed: false
      });
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const saveItem = async (itemId, updatedItem) => {
    if (!listDoc || !isOwner) return;

    try {
      const itemIndex = list.items.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return;

      const newItems = [...list.items];
      newItems[itemIndex] = { ...newItems[itemIndex], ...updatedItem };

      const updatedList = {
        ...list,
        items: newItems,
        lastEdited: Timestamp.now()
      };

      await saveList(updatedList);

      setEditingItemId(null); // Clear editing state
    } catch (error) {
      console.error("Error saving item:", error);
      setError("Failed to save item. Please try again.");
    }
  };

  const deleteItem = async (itemId) => {
    if (!listDoc || !isOwner) return;
    
    try {
      // Filter out the item to delete
      const newItems = list.items.filter(item => item.id !== itemId);
      
      const updatedList = {
        ...list,
        items: newItems,
        lastEdited: Timestamp.now()
      };

      await saveList(updatedList);
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const togglePacked = async (itemId) => {
    if (!listDoc || !isOwner) return;
    
    try {
      const itemIndex = list.items.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return;
      
      const oldItem = list.items[itemIndex];
      const newItem = { ...oldItem, packed: !oldItem.packed };
      
      // Create new items array with updated item
      const newItems = [...list.items];
      newItems[itemIndex] = newItem;
      
      const updatedList = {
        ...list,
        items: newItems,
        lastEdited: Timestamp.now()
      };

      await saveList(updatedList);
    } catch (error) {
      console.error("Error toggling packed status:", error);
    }
  };

  // Dropdown handlers
  const handleLocationChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      console.log("Opening location modal");
      setIsLocationModalOpen(true);
      // Reset the dropdown to a valid option to avoid having "add_new" persist
      setNewItem({ ...newItem, location: '' });
    } else {
      setNewItem({ ...newItem, location: value });
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      console.log("Opening category modal");
      setIsCategoryModalOpen(true);
      // Reset the dropdown to a valid option to avoid having "add_new" persist
      setNewItem({ ...newItem, category: '' });
    } else {
      setNewItem({ ...newItem, category: value });
    }
  };

  // Storage location and category management
  const addStorageLocation = async (e) => {
    e.preventDefault();
    if (!list || !list.id || !locationInput.trim() || !isOwner) return;

    try {
      const listRef = doc(db, "lists", list.id);
      const storageLocationsCollection = collection(listRef, "storageLocations");

      const docRef = await addDoc(storageLocationsCollection, {
        name: locationInput.trim()
      });

      // Update with the proper document ID from Firebase
      setStorageLocations([...storageLocations, { id: docRef.id, name: locationInput.trim() }]);
      setLocationInput(''); // Clear input for next entry
      
      // Auto-select this location if the user is adding an item
      if (!newItem.location) {
        setNewItem({ ...newItem, location: locationInput.trim() });
      }
    } catch (error) {
      console.error("Error adding storage location:", error);
    }
  };

  const deleteStorageLocation = async (locationId) => {
    if (!list || !list.id || !isOwner) return;

    try {
      const listRef = doc(db, "lists", list.id);
      const storageLocationRef = doc(listRef, "storageLocations", locationId);

      await deleteDoc(storageLocationRef);

      setStorageLocations(storageLocations.filter(location => location.id !== locationId));
    } catch (error) {
      console.error("Error deleting storage location:", error);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!list || !list.id || !categoryInput.trim() || !isOwner) return;

    try {
      const listRef = doc(db, "lists", list.id);
      const categoriesCollection = collection(listRef, "categories");

      const docRef = await addDoc(categoriesCollection, {
        name: categoryInput.trim()
      });

      // Update with the proper document ID from Firebase
      setCategories([...categories, { id: docRef.id, name: categoryInput.trim() }]);
      setCategoryInput(''); // Clear input for next entry
      
      // Auto-select this category if the user is adding an item
      if (!newItem.category) {
        setNewItem({ ...newItem, category: categoryInput.trim() });
      }
    } catch (error) {
      console.error("Error adding category:", error);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!list || !list.id || !isOwner) return;

    try {
      const listRef = doc(db, "lists", list.id);
      const categoryRef = doc(listRef, "categories", categoryId);

      await deleteDoc(categoryRef);

      setCategories(categories.filter(category => category.id !== categoryId));
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  // Sharing
  const shareList = () => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/shared/${listId}`;
    
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        alert("Read-only share link copied to clipboard! Anyone with this link can view this list (but not edit it).");
      })
      .catch(err => {
        console.error("Failed to copy URL:", err);
        alert(`Share this URL for read-only access: ${shareUrl}`);
      });
  };

  // Group view functions
  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const expandAllGroups = () => {
    setCollapsedGroups({});
  };

  const collapseAllGroups = (groups) => {
    const allCollapsed = Object.keys(groups).reduce((acc, groupName) => {
      acc[groupName] = true;
      return acc;
    }, {});
    
    setCollapsedGroups(allCollapsed);
  };

  if (loading) {
    return <div className="loading">Loading list...</div>;
  }

  if (!list) {
    return <div className="not-found">List not found</div>;
  }

  const tableProps = {
    viewDensity,
    groupBy,
    isOwner,
    togglePacked,
    saveItem,
    deleteItem,
    editingItemId,
    setEditingItemId,
    list,
    setList,
    storageLocations,
    categories,
  };

  return (
    <div className="list-container">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      
      <div className="navigation-section">
        <Button
          variant="secondary"
          onClick={() => navigate('/')}
          >
            ← Back to Dashboard
          </Button>
      </div>
      
      <ListHeader 
        list={list}
        isOwner={isOwner}
        viewOnly={viewOnly}
        handleEditPhoto={handleEditPhoto}
      />

      <ListPhoto 
        list={list}
        handleEditPhoto={handleEditPhoto}
        isOwner={isOwner}
        getMediumImageUrl={getMediumImageUrl}
      />
      
      {isOwner && (
        <div className="listing-share-section d-flex align-items-center">
          <Button 
            variant="primary"
            onClick={shareList}
            className="me-3"
          >
            Share List
          </Button>
          <span className="share-info">Share a read-only version with friends</span>
        </div>
      )}
      
      {isOwner && (
        <AddItemForm 
          newItem={newItem}
          setNewItem={setNewItem}
          addItem={addItem}
          handleLocationChange={handleLocationChange}
          handleCategoryChange={handleCategoryChange}
          storageLocations={storageLocations}
          categories={categories}
        />
      )}

      <div className="list-stats">
        <ViewControls 
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          viewDensity={viewDensity}
          setViewDensity={setViewDensity}
        />
      </div>
      
      <ProgressBar items={list.items || []} />
      
      <div className="items-container">
        <GroupedView
          items={list.items || []}
          groupBy={groupBy}
          collapsedGroups={collapsedGroups}
          toggleGroupCollapse={toggleGroupCollapse}
          expandAllGroups={expandAllGroups}
          collapseAllGroups={collapseAllGroups}
          tableProps={tableProps}
        />
      </div>

      <EditPhotoModal 
        isOpen={isEditPhotoModalOpen}
        onClose={() => setIsEditPhotoModalOpen(false)}
        editingList={editingList}
        setEditingList={setEditingList}
        savePhotoChanges={savePhotoChanges}
      />
      
      <LocationModal 
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        storageLocations={storageLocations}
        addStorageLocation={addStorageLocation}
        deleteStorageLocation={deleteStorageLocation}
        locationInput={locationInput}
        setLocationInput={setLocationInput}
      />
      
      <CategoryModal 
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        categories={categories}
        addCategory={addCategory}
        deleteCategory={deleteCategory}
        categoryInput={categoryInput}
        setCategoryInput={setCategoryInput}
      />
    </div>
  );
};

export default PackingList;